import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { conciergeApi } from '../../services/concierge-api-client';
import type { EventSiteData } from '../../types/event-data';
import { plural } from '../../lib/plural';

export interface ImportedEvent {
  title?: string;
  subtitle?: string;
  locationName?: string;
  dateStart?: string;
  dateEnd?: string;
  story?: string;
  externalRsvpUrl?: string;
}

export interface ImportedGuest {
  name: string;
  rsvp_status?: string | null;
  phone?: string | null;
  email?: string | null;
}

interface AuthImportPanelProps {
  onImported: (event: ImportedEvent, guests: ImportedGuest[]) => void;
}

type Phase = 'idle' | 'awaiting_login' | 'capturing' | 'review' | 'done';
type ImportMode = 'public' | 'authenticated';

/**
 * Host-driven import from a login-gated source (Partiful, etc.).
 * Host logs in inside a remote browser session; we capture the authenticated
 * page and extract event details + guest list. Backend is flag-guarded
 * (GROUPS_AUTH_IMPORT_ENABLED) and uses a mock provider for local dev.
 */
export default function AuthImportPanel({ onImported }: AuthImportPanelProps) {
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState<ImportMode>('public');
  const [phase, setPhase] = useState<Phase>('idle');
  const [importId, setImportId] = useState('');
  const [liveViewUrl, setLiveViewUrl] = useState('');
  const [authProvider, setAuthProvider] = useState('');
  const [error, setError] = useState('');
  const [guestCount, setGuestCount] = useState<number | null>(null);
  const [importedEvent, setImportedEvent] = useState<ImportedEvent | null>(null);
  const [importedGuests, setImportedGuests] = useState<ImportedGuest[]>([]);

  const sourceName = (() => {
    try {
      const host = new URL(url).hostname.replace(/^www\./, '');
      return host.includes('partiful') ? 'Partiful' : host || 'the source';
    } catch {
      return 'the source';
    }
  })();

  const activeStep = phase === 'idle' ? 0 : phase === 'awaiting_login' || phase === 'capturing' ? 1 : phase === 'review' ? 2 : 3;

  const normalizeUrl = () => {
    const trimmed = url.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    return `https://${trimmed}`;
  };

  const eventFromExtraction = (ev: EventSiteData): ImportedEvent => ({
    title: ev.event_name || undefined,
    locationName: ev.venue_city || ev.venue_name || undefined,
    dateStart: ev.event_date || undefined,
    dateEnd: ev.event_end_date || undefined,
    story: ev.description || undefined,
    externalRsvpUrl: normalizeUrl() || ev.rsvp_url || undefined,
  });

  const closeSession = async (id = importId) => {
    if (!id) return;
    try {
      await conciergeApi.closeAuthImport(id);
    } catch {
      // Best-effort cleanup. Backend sessions also expire server-side in production.
    }
  };

  const reset = async () => {
    await closeSession();
    setPhase('idle');
    setImportId('');
    setLiveViewUrl('');
    setAuthProvider('');
    setImportedEvent(null);
    setImportedGuests([]);
    setGuestCount(null);
    setError('');
  };

  const startPublicScrape = async () => {
    const normalized = normalizeUrl();
    if (!normalized) return;

    setUrl(normalized);
    setError('');
    setImportedEvent(null);
    setImportedGuests([]);
    setGuestCount(null);
    setPhase('capturing');

    try {
      const res = await conciergeApi.scrapePreview(normalized, 'event');
      setImportedEvent(eventFromExtraction(res.event_data));
      setImportedGuests([]);
      setGuestCount(0);
      setPhase('review');
    } catch (e: any) {
      setError(
        e.response?.data?.detail ||
          e.message ||
          'Could not read the public page. Try the sign-in scrape if the details are gated.',
      );
      setPhase('idle');
    }
  };

  const start = async () => {
    if (mode === 'public') {
      await startPublicScrape();
      return;
    }

    const normalized = normalizeUrl();
    if (!normalized) return;
    setUrl(normalized);
    setError('');
    setImportedEvent(null);
    setImportedGuests([]);
    setGuestCount(null);
    try {
      if (importId) await closeSession(importId);
      const res = await conciergeApi.startAuthImport(normalized);
      setImportId(res.import_id);
      setLiveViewUrl(res.live_view_url);
      setAuthProvider(res.provider || 'unknown');
      setPhase('awaiting_login');
    } catch (e: any) {
      setError(
        e.response?.status === 404
          ? 'Authenticated import is not enabled on this environment.'
          : e.response?.data?.detail || e.message || 'Could not start import',
      );
    }
  };

  const capture = async () => {
    setError('');
    setPhase('capturing');
    try {
      const res = await conciergeApi.captureAuthImport(importId);
      if (res.status === 'error' || !res.event_data) {
        throw new Error(res.error || 'Capture failed');
      }
      const ev = res.event_data;
      const guests = res.guests ?? [];
      setAuthProvider(res.provider || authProvider);
      setImportedEvent(eventFromExtraction(ev));
      setImportedGuests(guests);
      setGuestCount(guests.length);
      setPhase('review');
      await closeSession(importId);
    } catch (e: any) {
      setError(e.message || 'Capture failed');
      setPhase('awaiting_login');
    }
  };

  const applyReviewedImport = () => {
    if (!importedEvent) return;
    onImported(importedEvent, importedGuests);
    setPhase('done');
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: '#E5E7EB', bgcolor: '#F8FAF9' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <AutoAwesomeIcon sx={{ color: '#569469', fontSize: 20 }} />
        <Typography fontWeight={800}>Import event details</Typography>
        <Chip size="small" label="beta" sx={{ bgcolor: '#EEF5EF', color: '#386A48', fontWeight: 700 }} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Start with a public page scrape when the event details are visible. Use sign-in scrape only
        when the event or guest list is gated.
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
        {['Paste link', mode === 'public' ? 'Read page' : 'Sign in', 'Review', 'Apply'].map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <RadioGroup
        row
        value={mode}
        onChange={(event) => {
          setMode(event.target.value as ImportMode);
          setError('');
          if (phase !== 'idle') void reset();
        }}
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          value="public"
          control={<Radio size="small" />}
          label="Public page scrape"
        />
        <FormControlLabel
          value="authenticated"
          control={<Radio size="small" />}
          label="Sign-in scrape"
        />
      </RadioGroup>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#FFFFFF' }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          {mode === 'authenticated' ? <LockOutlinedIcon sx={{ color: '#386A48', fontSize: 20, mt: 0.25 }} /> : null}
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>
              {mode === 'public' ? 'What happens with public scrape?' : 'What happens in the sign-in window?'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mode === 'public'
                ? 'Tripsby reads the public event page only. This is best for titles, dates, locations, descriptions, and RSVP links when they are visible without logging in.'
                : `Tripsby opens an isolated cloud browser for this one import. You sign in directly to ${sourceName}; Tripsby never asks for or stores your password. After you click import, we read the visible event page once, close the session, and show you a review screen before saving anything.`}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {mode === 'authenticated' && authProvider === 'mock' ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This environment is using mock import data. To test a real Partiful login, set
          GROUPS_AUTH_IMPORT_PROVIDER=self_hosted and point AUTH_IMPORT_BROWSER_RENDERER_URL at the browser renderer.
        </Alert>
      ) : null}
      {mode === 'authenticated' && authProvider === 'self_hosted' ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Real self-hosted browser import is active. The sign-in session should open on the pasted event URL.
        </Alert>
      ) : null}

      {phase === 'idle' || phase === 'awaiting_login' ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            placeholder="https://partiful.com/e/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Button variant="outlined" onClick={start} disabled={!url.trim()} sx={{ whiteSpace: 'nowrap' }}>
            {phase === 'awaiting_login'
              ? 'Restart session'
              : mode === 'public'
                ? 'Scrape public page'
                : 'Start secure import'}
          </Button>
        </Stack>
      ) : null}

      {phase === 'awaiting_login' ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Sign in to {sourceName} in the secure session, open the guest list if needed, then come back here.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              variant="contained"
              component={Link}
              href={liveViewUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Sign in to {sourceName}
            </Button>
            <Button variant="contained" color="success" onClick={capture}>
              I signed in - review import
            </Button>
            <Button variant="text" color="inherit" onClick={reset}>
              Cancel
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The session is hosted in an isolated cloud browser and deleted after capture.
          </Typography>
        </Box>
      ) : null}

      {phase === 'capturing' ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            {mode === 'public'
              ? 'Reading the public page and extracting details...'
              : 'Reading the visible page once and closing the session...'}
          </Typography>
        </Stack>
      ) : null}

      {phase === 'review' && importedEvent ? (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Review the imported details before applying them. Nothing has been saved to the group yet.
          </Alert>
          <Stack spacing={1.25}>
            <Typography variant="subtitle2" fontWeight={800}>Event details</Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#FFF' }}>
              <Stack spacing={0.75}>
                <Typography variant="body2"><strong>Title:</strong> {importedEvent.title || 'Not found'}</Typography>
                <Typography variant="body2"><strong>Location:</strong> {importedEvent.locationName || 'Not found'}</Typography>
                <Typography variant="body2"><strong>Dates:</strong> {importedEvent.dateStart || 'Not found'}{importedEvent.dateEnd ? ` - ${importedEvent.dateEnd}` : ''}</Typography>
              </Stack>
            </Paper>

            <Divider sx={{ my: 1 }} />

            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={800}>Guest preview</Typography>
              <Typography variant="caption" color="text.secondary">
                {importedGuests.length} guest{importedGuests.length === 1 ? '' : 's'} found
              </Typography>
            </Stack>
            {importedGuests.length ? (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 220 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>RSVP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importedGuests.slice(0, 8).map((guest, index) => (
                      <TableRow key={`${guest.name}-${index}`}>
                        <TableCell>{guest.name}</TableCell>
                        <TableCell>{guest.rsvp_status || 'Unknown'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {mode === 'public'
                  ? 'Public scrape does not import gated guest lists. Use sign-in scrape if you need guests.'
                  : 'No guests were found. You can still apply the event details and add invitees later.'}
              </Typography>
            )}
            {importedGuests.length > 8 ? (
              <Typography variant="caption" color="text.secondary">
                Showing the first 8 guests. The full imported list will be saved when you create the group.
              </Typography>
            ) : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
              <Button variant="contained" color="success" onClick={applyReviewedImport}>
                Apply to group form
              </Button>
              <Button variant="outlined" onClick={reset}>
                Start over
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}

      {phase === 'done' ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          Imported event details{guestCount != null ? ` and ${plural(guestCount, 'guest')}` : ''}. Review the
          fields below before creating the group.
        </Alert>
      ) : null}
    </Paper>
  );
}
